import { z } from "zod";

/**
 * Sözleşme ÇALIŞMA METNİ (CLOS-118 `[086]`).
 *
 * ### ⚠ Kanonik biçim YAPISAL, HTML DEĞİL (ADR-0026)
 *
 * HTML saklamak her okuma yolunda temizleme borcu demek; bir gün bir yol temizlemeyi
 * atlarsa sözleşme metni saklanmış bir XSS taşıyıcısına döner. Ayrıca HTML bir gösterim
 * biçimidir: PDF üretimi, madde karşılaştırma ve madde kütüphanesi ondan yapıyı geri
 * çıkarmak zorunda kalırdı.
 *
 * ### ⚠ Biçimlendirme: BLOK düzeyi + KARAKTER düzeyi (CLOS-118'in 20 Ağustos kararı)
 *
 * Bu dosya bir süre yalnız `paragraph` ve `bullet` taşıdı ve gerekçesi şuydu: *"karakter
 * düzeyi işaretler ofsetle tutulur, ofsetler metin düzenlendiğinde kayar ... bugün hiçbir
 * ekranın üretmediği bir veri biçimini yaymak olurdu (ADR-0014)."*
 *
 * ⚠ O GEREKÇE ARTIK GEÇERSİZ ve nasıl geçersizleştiği kayda değer: CLOS-118'e 20 Ağustos
 * 2026'da kanonik bir ürün kararı eklendi — *"başlık, paragraf, kalın/italik,
 * liste/numaralandırma, hizalama kullanıcıya doğal doküman düzenleme deneyimi sunar"* ve
 * *"bu karar farklı bir birincil düzenleme modeline dönüştürülemez"*. Yani karar geldi,
 * şema güncellenmedi; yorum kararın ÖNCESİNDEN kalmıştı ve "kart istemiyor" diyordu, oysa
 * kart istiyordu. Kartın Done olması bunu görünmez kıldı (CLOS-634 aynı kökten).
 *
 * ### ⚠ `text` DÜZ METİN OLARAK KALIYOR — kırmızı çizgi
 *
 * Biçim bilgisi `text`'in ÜSTÜNE ekleniyor, onun YERİNE geçmiyor. Sebep ölçüldü: müzakere
 * `targetBlockIndex` + `currentText` ile bloğa bağlanıyor, karşılaştırma ve dışa aktarma da
 * `text` okuyor. `text`'i işaretli bir yapıya çevirseydik bu üç yol birden sessizce
 * bozulurdu. Yeni alanların hepsi OPSİYONEL: bugünkü satırlar olduğu gibi geçerli kalıyor
 * ve hiçbir migration gerekmiyor.
 *
 * ⚠ Bunun bedeli: `marks` ofsetleri `text` ile TUTARLI olmak zorunda. Şema bunu doğruluyor
 * (aşağıdaki `superRefine`); tutarsız ofset sessizce kırpılmıyor, reddediliyor.
 */

export const CONTRACT_BLOCK_KINDS = ["paragraph", "bullet", "ordered", "heading"] as const;
export type ContractBlockKind = (typeof CONTRACT_BLOCK_KINDS)[number];

export const CONTRACT_BLOCK_KIND_NAMES: Record<ContractBlockKind, string> = {
  paragraph: "Paragraf",
  bullet: "Madde işareti",
  ordered: "Numaralı madde",
  heading: "Ara başlık",
};

/** Hizalama YOKSA varsayılan sola: `undefined` "hizalama yok" demek, "sola daya" değil. */
export const CONTRACT_BLOCK_ALIGNMENTS = ["left", "center", "right", "justify"] as const;
export type ContractBlockAlignment = (typeof CONTRACT_BLOCK_ALIGNMENTS)[number];

export const CONTRACT_MARK_TYPES = ["bold", "italic", "underline"] as const;
export type ContractMarkType = (typeof CONTRACT_MARK_TYPES)[number];

/**
 * Karakter aralığı işareti.
 *
 * ⚠ Aralık YARI AÇIK: `[start, end)`. Kapalı yazsaydık boş aralık (`start === end`)
 * bir karakter kaplıyormuş gibi görünürdü ve editörden gelen sıfır uzunluklu seçim
 * sessizce bir karaktere yayılırdı.
 */
export const contractMarkSchema = z.object({
  type: z.enum(CONTRACT_MARK_TYPES, "Geçerli bir işaret türü gereklidir"),
  start: z.number().int().min(0, "İşaret başlangıcı negatif olamaz"),
  end: z.number().int().min(1, "İşaret bitişi en az 1 olmalıdır"),
});
export type ContractMark = z.infer<typeof contractMarkSchema>;

export const contractBlockSchema = z
  .object({
    /**
     * STABLE BLOCK IDENTITY (CLOS-1233 — DOCX Kanonu §11).
     *
     * ⚠ GİRDİDE opsiyonel, ÇIKTIDA her zaman dolu. Eksikse sunucu atıyor
     * (`lib/documentIdentity.ts`): kimliğin varlığını istemcinin doğru davranmasına
     * bırakamayız — eski bir sekme ya da doğrudan API çağrısı kimliksiz belge gönderebilir
     * ve o belge kaydedilirse ona çapa atan her şey adressiz kalır.
     *
     * ⚠ Zorunlu KILINMADI çünkü mevcut kayıtlı belgelerin blokları kimliksiz ve
     * `contract_versions` yeniden yazılmıyor (kanonun 1. kuralı). Zorunlu kılsaydık eski
     * bir belgeyi okuyup geri kaydetmek 422 verirdi.
     */
    id: z.uuid("Geçerli bir blok kimliği gereklidir").optional(),
    kind: z.enum(CONTRACT_BLOCK_KINDS, "Geçerli bir blok türü seçilmelidir"),
    /**
     * DÜZ METİN. Kırpma yok, temizleme yok: metin geldiği gibi saklanıyor. Sessizce
     * kırpsaydık kartın *"biçim kaybı kullanıcıya bildirilir"* kuralını, kaybı hiç
     * bildirmeden delerdik.
     */
    text: z
      .string({ error: "Blok metni zorunludur" })
      .min(1, "Boş blok kaydedilemez")
      .max(20_000, "Bir blok en fazla 20.000 karakter olabilir"),
    /** Yalnız `heading` için anlamlı; 1 belge başlığı değil, madde İÇİ ara başlık seviyesi. */
    level: z.number().int().min(1).max(3).optional(),
    align: z.enum(CONTRACT_BLOCK_ALIGNMENTS).optional(),
    marks: z
      .array(contractMarkSchema)
      .max(1000, "Bir blokta en fazla 1000 işaret olabilir")
      .optional(),
  })
  .superRefine((blok, ctx) => {
    for (const [sira, isaret] of (blok.marks ?? []).entries()) {
      /**
       * ⚠ ÜÇ AYRI KUSUR, ÜÇ AYRI MESAJ. Tek "geçersiz işaret" mesajı yazsaydık, hangi
       * ucun bozuk olduğunu ne kullanıcı ne de kusuru arayan biri görebilirdi.
       */
      if (isaret.end <= isaret.start) {
        ctx.addIssue({
          code: "custom",
          path: ["marks", sira],
          message: "İşaret bitişi başlangıcından sonra olmalıdır",
        });
      }
      if (isaret.end > blok.text.length) {
        ctx.addIssue({
          code: "custom",
          path: ["marks", sira],
          message: "İşaret blok metninin dışına taşamaz",
        });
      }
    }
  });
export type ContractBlock = z.infer<typeof contractBlockSchema>;

export const contractClauseSchema = z.object({
  /** İstemcinin ürettiği kimlik: madde taşınınca da aynı kalıyor, sıra numarası değil. */
  id: z.uuid("Geçerli bir madde kimliği gereklidir"),
  /** Başlık BOŞ OLABİLİR: numarasız serbest bölüm de bir maddedir. */
  title: z.string().max(300, "Madde başlığı en fazla 300 karakter olabilir"),
  blocks: z.array(contractBlockSchema).max(500, "Bir maddede en fazla 500 blok olabilir"),
});
export type ContractClause = z.infer<typeof contractClauseSchema>;

export const contractDocumentSchema = z.object({
  clauses: z.array(contractClauseSchema).max(1000, "Sözleşmede en fazla 1000 madde olabilir"),
});
export type ContractDocument = z.infer<typeof contractDocumentSchema>;

/**
 * Kaydetme girdisi.
 *
 * ⚠ `revision` ZORUNLU: hangi hâlin üzerine yazdığını söylemeyen bir kaydetme, başkasının
 * değişikliğini sessizce ezer. Koşul `UPDATE`'in içinde uygulanıyor — "önce oku, sürüm
 * tutuyorsa yaz" deseni iki eşzamanlı isteği de kontrolden geçirirdi.
 */
export const saveContractContentSchema = z.object({
  document: contractDocumentSchema,
  revision: z.coerce.number().int().min(0, "Geçerli bir sürüm gereklidir"),
});
export type SaveContractContentInput = z.infer<typeof saveContractContentSchema>;

/**
 * Metnin DÜZENLENEMEZ olma sebepleri — makine tarafından okunabilir.
 *
 * ⚠ Sebep kodu şart: arayüz "düzenlenemez" bilgisiyle ne yapacağını sebebe göre seçiyor
 * (salt okunur göster / dosya kartına yönlendir / yetki açıkla). Yalnız bir cümle
 * gönderseydik istemci metni ayrıştırmak zorunda kalırdı (`docs/api-hata-sozlesmesi.md`).
 */
export const CONTRACT_CONTENT_LOCK_REASONS = [
  /** Yaşam döngüsü durumu metni dondurmuş (onaylı, mutabık, imzalı, arşiv...). */
  "status_locked",
  /** Sözleşme yüklenen bir dosyadan doğdu; uygulama içi metni yok (`[071]`). */
  "file_backed",
  /** Kullanıcının düzenleme yetkisi yok. */
  "no_permission",
] as const;
export type ContractContentLockReason = (typeof CONTRACT_CONTENT_LOCK_REASONS)[number];

export const CONTRACT_CONTENT_LOCK_MESSAGES: Record<ContractContentLockReason, string> = {
  status_locked: "Bu durumdaki sözleşmenin metni değiştirilemez.",
  file_backed:
    "Bu sözleşmenin metni yüklenen dosyada; uygulama içinde düzenlenebilir bir metni yok.",
  no_permission: "Sözleşme metnini düzenleme yetkiniz yok.",
};

/**
 * DURUM KİLİDİNİN AÇIK CÜMLESİ.
 *
 * ⚠ *"Bu durumdaki sözleşmenin metni değiştirilemez"* üç soruyu birden cevapsız
 * bırakıyordu: hangi durumdayım, hangi durumlarda düzenlenebilir, şimdi ne yapacağım.
 * Saha ölçümü (4 Eylül 2026): kullanıcı "Aktif" hâle aldığı bir sözleşmede bu cümleyi
 * gördü ve neden düzenleyemediğini ekrandan öğrenemedi — kilit doğruydu, açıklama yoktu.
 *
 * ⚠ CÜMLE BURADA, EKRANDA DEĞİL: aynı kilit birden fazla yüzeyde çıkıyor ve her yüzeyin
 * kendi cümlesini yazması, aynı kuralın farklı kelimelerle anlatılması olurdu.
 *
 * ⚠ Durum adı ya da düzenlenebilir durum listesi YOKSA cümle sadeleşiyor, UYDURMUYOR:
 * bilinmeyen bir durumda "şu durumlarda düzenlenebilir" demek, kullanıcıyı olmayan bir
 * yola göndermek olurdu.
 */
export function contractContentLockExplanation(input: {
  reason: ContractContentLockReason;
  statusLabel?: string | null;
  editableStatusLabels?: readonly string[];
  scanned?: boolean;
  ocrPageCount?: number | null;
  ocrConfidence?: number | null;
}): string {
  /**
   * ⚠ TARANMIŞ BELGE KENDİ CÜMLESİNİ HAK EDİYOR — saha ölçümü (4 Eylül 2026).
   *
   * Ekran yalnız "metni yüklenen dosyada" diyordu ve kullanıcı orada kalıyordu: belgenin
   * TARANMIŞ olduğunu, metin katmanı taşımadığını, sistemin OCR ile okuduğunu ve o metnin
   * neden sözleşme metni sayılmadığını hiçbir yerde göremiyordu. On altı sayfalık bir
   * sözleşme yükleyen kullanıcı "ürün bunu okuyamıyor" sanıyordu; oysa okumuştu.
   */
  if (input.reason === "file_backed" && input.scanned) {
    const okundu =
      input.ocrPageCount && input.ocrConfidence
        ? ` Sistem belgeyi okudu: ${input.ocrPageCount} sayfa, ortalama %${Math.round(input.ocrConfidence)} tanıma güveni.`
        : "";
    return (
      "Bu belge taranmış: içinde seçilebilir metin yok, sayfalar görüntü olarak duruyor." +
      okundu +
      " Okunan metin ön izlemede ve AI analizinde kullanılıyor, ancak sözleşmenin kanonik" +
      " metni sayılmıyor — tanıma hatası taşıyabilir ve imzalanacak metin tahmine" +
      " dayanamaz. Uygulama içinde düzenlemek için metin katmanı olan bir dosyadan" +
      " (DOCX ya da metin taşıyan PDF) yeni bir versiyon üretin."
    );
  }
  if (input.reason !== "status_locked") return CONTRACT_CONTENT_LOCK_MESSAGES[input.reason];

  const durum = input.statusLabel?.trim();
  const acilanlar = (input.editableStatusLabels ?? []).filter((ad) => ad.trim().length > 0);

  const bas = durum
    ? `Sözleşme “${durum}” durumunda ve bu durumdaki metin değiştirilemez.`
    : CONTRACT_CONTENT_LOCK_MESSAGES.status_locked;

  const nerede =
    acilanlar.length > 0 ? ` Metin yalnız ${acilanlar.join(", ")} durumlarında düzenlenir.` : "";

  /**
   * ⚠ ÇIKIŞ YOLU HER ZAMAN YAZILIYOR: yürürlükteki bir metni değiştirmenin doğru yolu
   * üzerine yazmak değil, yeni bir versiyon üretmek. Kilidin sebebi de bu — imzalanan
   * metinle sistemdeki metin ayrışmasın.
   */
  return `${bas}${nerede} Değişiklik gerekiyorsa yeni bir versiyon üretin; yürürlükteki metin olduğu gibi kalır.`;
}

/**
 * KAYIT ERİŞİMİ EKRANA İKİ ALANDAN ULAŞIYOR — hangisi nereye (CLOS-888).
 *
 * Aynı gerçeğin iki farklı soruya verdiği cevap var ve ikisi de `hasRecordAccess`'ten
 * türüyor (yazma kapısının kendi fonksiyonu), yani inşa gereği tutarlılar:
 *
 * | alan | soru | tüketicisi |
 * |---|---|---|
 * | `ContractDetail.viewerAccessLevel` | bu KAYITTA düzeyim ne? | eylem yüzeyleri: düğmeler, bölümler |
 * | `ContractContentView.editable` + `lockReason` | bu METNİ düzenleyebilir miyim? | içerik editörü |
 *
 * ⚠ İçerik sorusu kayıt düzeyinden DAHA DAR: yazma erişimi olsa bile metin, yaşam
 * döngüsü durumu (`status_locked`) ya da kaynağı (`file_backed`) yüzünden kilitli
 * olabilir. Bu yüzden editör `viewerAccessLevel`'a bakmaz — daha geniş bir cevaba bakıp
 * kilitli metni açardı.
 *
 * ⚠ YENİ BİR YÜZEY BU İKİSİNDEN UYGUN OLANI SEÇER, ÜÇÜNCÜSÜNÜ AÇMAZ. Üçüncü bir hesap
 * eklemek, bir gün ayrışacak üçüncü bir gerçek kaynağı demektir — bu kartın kapattığı
 * kusurun ta kendisi (ekran iki ayrı düzey beyanıyla konuşuyordu).
 */
export type ContractContentView = {
  document: ContractDocument;
  /** Kaydetmede geri gönderilen sürüm; boş metinde 0. */
  revision: number;
  editable: boolean;
  /** `editable` false ise DOLU, true ise `null`. */
  lockReason: ContractContentLockReason | null;
  /**
   * Sözleşmenin şu anki yaşam döngüsü durumunun EKRAN ADI ve metnin hangi durumlarda
   * düzenlenebildiği — kilit cümlesi bunlarla anlam kazanıyor.
   *
   * ⚠ Katalogdan geliyor, istemcide türetilmiyor: akışa bir durum eklendiğinde ekran
   * eski listeyi göstermeye devam ederdi.
   */
  statusLabel: string | null;
  editableStatusLabels: string[];
  /**
   * TARANMIŞ BELGE DURUMU — ekran kilidi açıklarken buna bakıyor.
   *
   * ⚠ `scanned`: dosyada metin katmanı yok, çıkarım `ocr_required` verdi.
   * `ocrPageCount` ve `ocrConfidence` dolu ise OCR koştu ve YARDIMCI metin üretti.
   *
   * ⚠ OCR metni SÖZLEŞME METNİ DEĞİL (kanonik karar): "taranmış PDF kaynak görüntüsü
   * OCR olmadan görüntülenebilir; aranabilir yardımcı metin yalnız OCR sonucu varsa
   * sunulur." %90 güvenle okunmuş bir metni kanonik metin yapmak, avukatın OCR hatası
   * taşıyan bir belgeyi düzenleyip imzalamasına yol açardı.
   */
  scanned: boolean;
  ocrPageCount: number | null;
  ocrConfidence: number | null;
  updatedAt: string | null;
  updatedByName: string | null;
  /**
   * Yüklenen belgeden metin AKTARILABİLİR mi (CLOS-634).
   *
   * ⚠ Karar sunucuda: çalışma metni boş VE en güncel versiyonun çıkarımı başarılı olmalı.
   * İstemci "belge boşsa düğmeyi göster" deseydi, taranmış bir PDF'te de düğme çıkar ve
   * kullanıcı tıkladığında hata alırdı.
   */
  adoptableFromUpload: boolean;
  /**
   * DÖNÜŞÜM KAYIPLARININ TÜRKÇE ADLARI — aktarma noktasında gösterilmek üzere (CLOS-634).
   *
   * ⚠ Metin SUNUCUDAN geliyor: aynı sözlük ön izleme yüzeyinde de kullanılıyor
   * (`preview.ts`). İstemci kendi cümlesini yazsaydı aynı kayıp iki ekranda iki farklı
   * biçimde anlatılırdı.
   *
   * ⚠ Boş dizi "kayıp yok" demek. Aktarım uygun değilken de boş: uygun olmayan bir
   * aktarımın kaybını göstermek, olmayan bir işlemi anlatmak olurdu.
   */
  conversionLossNames: string[];
};

/** Boş belge — hiç metin girilmemiş sözleşmenin kanonik hâli. */
export const BOS_BELGE: ContractDocument = { clauses: [] };

/**
 * EDİTÖR POLİTİKASI (CLOS-119 `[087]`).
 *
 * ⚠ Kartın açık kararı: *"Otomatik kaydetme gecikmesi, yeniden deneme aralığı ve çakışma
 * uyarısı eşikleri KONFİGÜRE EDİLECEKTİR"* ve *"parametreler hard-code edilmez."* Değerler
 * `config/contractEditor.json`'da ve arayüze SUNUCUDAN geliyor: istemciye gömseydik, bir
 * olay anında gecikmeyi büyütmek yeni bir sürüm çıkarmayı gerektirirdi.
 */
export const contractEditorPolicySchema = z.object({
  /** Son tuş vuruşundan sonra otomatik kaydetmeye kadar beklenen süre. */
  autosaveDelayMs: z.number().int().min(250).max(60_000),
  /** Ağ hatasından sonra yeniden denemeye kadar beklenen süre. */
  retryDelayMs: z.number().int().min(250).max(300_000),
  /** Kaç deneme sonrasında vazgeçilip kullanıcıya bırakılıyor. */
  maxRetryAttempts: z.number().int().min(0).max(20),
  /**
   * Kaç başarısız denemeden sonra *"değişiklikleriniz henüz kaydedilmedi"* uyarısı
   * belirginleşiyor. Kart: *"bağlantı kesintisinde ... KAYBOLMA RİSKİ AÇIKLANIR."*
   */
  pendingWarningAfterFailures: z.number().int().min(1).max(20),
});
export type ContractEditorPolicy = z.infer<typeof contractEditorPolicySchema>;

/**
 * Kaydetme durumu — kullanıcıya GÖSTERİLEN tek gerçek.
 *
 * ⚠ Kart: *"sunucuya BAŞARIYLA YAZILMADAN kaydedildi olarak gösterilmez."* Bu yüzden
 * "bekliyor" ile "kaydedildi" ayrı durumlar ve aralarındaki geçişi yalnız sunucunun 200'ü
 * yapıyor.
 */
export const CONTRACT_SAVE_STATES = [
  "saved",
  "dirty",
  "saving",
  "pending",
  "conflict",
  "blocked",
] as const;
export type ContractSaveState = (typeof CONTRACT_SAVE_STATES)[number];

export const CONTRACT_SAVE_STATE_NAMES: Record<ContractSaveState, string> = {
  saved: "Tüm değişiklikler kaydedildi",
  dirty: "Kaydedilmemiş değişiklikler var",
  saving: "Kaydediliyor…",
  pending: "Değişiklikler bekliyor — sunucuya henüz yazılamadı",
  conflict: "Metin başka biri tarafından değiştirildi",
  blocked: "Bu metin artık düzenlenemiyor",
};
